import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ContentBlockerField, ESupportedLanguage, ServiceTemplateField, TemplateMetaDataField } from "@devowl-wp/api-real-cookie-banner";
import { Trans } from "@devowl-wp/react-frontend";
import { getLanguageIcon } from "../translation.js";
/**
 * Translates a field name to its corresponding label.
 *
 * @param fieldName The name of the field to translate
 * @param t The translation function
 * @returns The translated field name or the original field name if no translation is found
 */ function getFieldNameTranslated(fieldName, t) {
    switch(fieldName){
        // Template Metadata
        case TemplateMetaDataField.Name:
            return t("Name");
        case TemplateMetaDataField.Headline:
            return t("Headline");
        case TemplateMetaDataField.SubHeadline:
            return t("Subheadline");
        case TemplateMetaDataField.Identifier:
            return t("Unique identifier");
        case TemplateMetaDataField.EnabledWhenOneOf:
            return t("Enabled when one of");
        case TemplateMetaDataField.RecommendedWhenOneOf:
            return t("Recommended when one of");
        case TemplateMetaDataField.IsDeleted:
            return t("Template deleted");
        case TemplateMetaDataField.IsHidden:
            return t("Visibility");
        case TemplateMetaDataField.Logo:
            return t("Logo");
        case TemplateMetaDataField.SuccessorOfIdentifier:
            return t("Successor of template");
        case TemplateMetaDataField.Tier:
            return t("Product tier");
        case TemplateMetaDataField.Version:
            return t("Version");
        case TemplateMetaDataField.MinRequiredRcbVersion:
            return /*#__PURE__*/ _jsxs(Trans, {
                children: [
                    "Minimum Real Cookie",
                    /*#__PURE__*/ _jsx("br", {}),
                    "Banner version"
                ]
            });
        // Service Template Fields
        case ServiceTemplateField.Provider:
            return t("Provider");
        case ServiceTemplateField.ProviderNotice:
            return t("Provider notice");
        case ServiceTemplateField.ProviderPrivacyPolicyUrl:
            return t("Provider privacy policy URL");
        case ServiceTemplateField.ProviderLegalNoticeUrl:
            return t("Provider legal notice URL");
        case ServiceTemplateField.IsProviderCurrentWebsite:
            return t("Provider is current site");
        case ServiceTemplateField.GroupNotice:
            return t("Group notice");
        case ServiceTemplateField.Group:
            return t("Group");
        case ServiceTemplateField.LegalBasis:
            return t("Legal basis");
        case ServiceTemplateField.LegalBasisNotice:
            return t("Legal basis notice");
        case ServiceTemplateField.Purposes:
            return t("Purposes");
        case ServiceTemplateField.TechnicalHandlingNotice:
            return t("Technical handling notice");
        case ServiceTemplateField.TechnicalDefinitions:
            return t("Technical definitions");
        case ServiceTemplateField.DynamicFields:
            return t("Dynamic fields");
        case ServiceTemplateField.CreateContentBlockerNotice:
            return t("Create content blocker notice");
        case ServiceTemplateField.CodeOptIn:
            return t("Code executed on opt-in");
        case ServiceTemplateField.CodeOptOut:
            return t("Code executed on opt-out");
        case ServiceTemplateField.CodeOnPageLoad:
            return t("Code executed on page load");
        case ServiceTemplateField.ExecuteCodeOptInWhenNoTagManagerConsentIsGiven:
            return t("Code executed on opt-in");
        case ServiceTemplateField.ExecuteCodeOptOutWhenNoTagManagerConsentIsGiven:
            return t("Code executed on opt-out");
        case ServiceTemplateField.ExecutePriority:
            return t("Priority");
        case ServiceTemplateField.GoogleConsentModeConsentTypes:
            return /*#__PURE__*/ _jsxs(Trans, {
                children: [
                    "Requested consent types",
                    /*#__PURE__*/ _jsx("br", {}),
                    "for Google Consent Mode"
                ]
            });
        case ServiceTemplateField.IsEmbeddingOnlyExternalResources:
            return /*#__PURE__*/ _jsxs(Trans, {
                children: [
                    "Embedding only",
                    /*#__PURE__*/ _jsx("br", {}),
                    "external resources"
                ]
            });
        case ServiceTemplateField.DataProcessingInCountries:
            return t("Data processing countries");
        case ServiceTemplateField.DataProcessingInCountriesSpecialTreatments:
            return /*#__PURE__*/ _jsxs(Trans, {
                children: [
                    "Safety mechanisms for",
                    /*#__PURE__*/ _jsx("br", {}),
                    "data transmission"
                ]
            });
        case ServiceTemplateField.IsCdn:
            return t("Service is CDN");
        case ServiceTemplateField.SccConclusionInstructionsNotice:
            return t("Instructions for concluding SCCs");
        // Content Blocker Fields
        case ContentBlockerField.Description:
            return t("Description");
        case ContentBlockerField.RuleNotice:
            return t("Rule notice");
        case ContentBlockerField.Rules:
            return t("Rules");
        case ContentBlockerField.RuleGroups:
            return t("Rule Groups");
        case ContentBlockerField.ExampleEmbedCodes:
            return t("Example embed codes");
        case ContentBlockerField.IsVisual:
            return t("Visual content blocker");
        case ContentBlockerField.IsVisualDarkMode:
            return t("Dark mode");
        case ContentBlockerField.VisualBlur:
            return t("Blur image");
        case ContentBlockerField.VisualType:
            return t("Visual type");
        case ContentBlockerField.VisualContentType:
            return t("Visual content type");
        case ContentBlockerField.VisualHeroButtonText:
            return t("Hero button text");
        case ContentBlockerField.ServiceTemplateIdentifiers:
            return t("Connected services");
        case ContentBlockerField.ShouldForceToShowVisual:
            return t("Force visual content blocker");
        case ServiceTemplateField.ShouldUncheckContentBlockerCheckboxWhenOneOf:
            return /*#__PURE__*/ _jsx(Trans, {
                children: /*#__PURE__*/ _jsxs("p", {
                    children: [
                        "Uncheck",
                        /*#__PURE__*/ _jsx("br", {}),
                        "content blocker checkbox",
                        /*#__PURE__*/ _jsx("br", {}),
                        "when one of"
                    ]
                })
            });
        case ServiceTemplateField.ShouldUncheckContentBlockerCheckbox:
            return /*#__PURE__*/ _jsxs(Trans, {
                children: [
                    "Uncheck",
                    /*#__PURE__*/ _jsx("br", {}),
                    "content blocker checkbox"
                ]
            });
        default:
            return fieldName;
    }
}
/**
 * Returns the supported languages with their corresponding label and icon.
 *
 * @param t The translation function
 * @returns The supported languages with their corresponding label and icon
 */ function getSupportedLanguageOptions(t) {
    const languageOptions = [
        {
            language: ESupportedLanguage.en,
            label: t("English (default)"),
            icon: getLanguageIcon(ESupportedLanguage.en)
        },
        {
            language: ESupportedLanguage.de_formal,
            label: t("German formal"),
            icon: getLanguageIcon(ESupportedLanguage.de_formal)
        },
        {
            language: ESupportedLanguage.de_informal,
            label: t("German informal"),
            icon: getLanguageIcon(ESupportedLanguage.de_informal)
        },
        {
            language: ESupportedLanguage.fr,
            label: t("Français"),
            icon: getLanguageIcon(ESupportedLanguage.fr)
        },
        {
            language: ESupportedLanguage.es,
            label: t("Español"),
            icon: getLanguageIcon(ESupportedLanguage.es)
        },
        {
            language: ESupportedLanguage.it,
            label: t("Italian"),
            icon: getLanguageIcon(ESupportedLanguage.it)
        },
        {
            language: ESupportedLanguage.nl_formal,
            label: t("Dutch formal"),
            icon: getLanguageIcon(ESupportedLanguage.nl_formal)
        },
        {
            language: ESupportedLanguage.nl_informal,
            label: t("Dutch informal"),
            icon: getLanguageIcon(ESupportedLanguage.nl_informal)
        },
        {
            language: ESupportedLanguage.da,
            label: t("Danish"),
            icon: getLanguageIcon(ESupportedLanguage.da)
        },
        {
            language: ESupportedLanguage.sv,
            label: t("Swedish"),
            icon: getLanguageIcon(ESupportedLanguage.sv)
        },
        {
            language: ESupportedLanguage.no,
            label: t("Norwegian"),
            icon: getLanguageIcon(ESupportedLanguage.no)
        },
        {
            language: ESupportedLanguage.cs,
            label: t("Czech"),
            icon: getLanguageIcon(ESupportedLanguage.cs)
        },
        {
            language: ESupportedLanguage.pt,
            label: t("Portuguese"),
            icon: getLanguageIcon(ESupportedLanguage.pt)
        },
        {
            language: ESupportedLanguage.pl,
            label: t("Polish"),
            icon: getLanguageIcon(ESupportedLanguage.pl)
        },
        {
            language: ESupportedLanguage.el,
            label: t("Greek"),
            icon: getLanguageIcon(ESupportedLanguage.el)
        },
        {
            language: ESupportedLanguage.rm,
            label: t("Romanian"),
            icon: getLanguageIcon(ESupportedLanguage.rm)
        },
        {
            language: ESupportedLanguage.sk,
            label: t("Slovak"),
            icon: getLanguageIcon(ESupportedLanguage.sk)
        },
        {
            language: ESupportedLanguage.fi,
            label: t("Finnish"),
            icon: getLanguageIcon(ESupportedLanguage.fi)
        },
        {
            language: ESupportedLanguage.hu,
            label: t("Hungarian"),
            icon: getLanguageIcon(ESupportedLanguage.hu)
        },
        {
            language: ESupportedLanguage.hr,
            label: t("Croatian"),
            icon: getLanguageIcon(ESupportedLanguage.hr)
        },
        {
            language: ESupportedLanguage.sl,
            label: t("Slovenian"),
            icon: getLanguageIcon(ESupportedLanguage.sl)
        }
    ];
    languageOptions.sort((a, b)=>a.label.localeCompare(b.label));
    return languageOptions;
}
export { getFieldNameTranslated, getSupportedLanguageOptions };
